import type { PlayerError } from "@hybrid-iptv/player";
import { usePlayerStore } from "../player/playerStore.js";
import { useChannelHealthStore } from "./channelHealthStore.js";

/**
 * Which live-channel errors count as a failed try toward hiding the channel (owner, 2026-10-21:
 * "if a channel has an error message after normal protocol hide it but don't lose it", and
 * "don't lose channels that actually work").
 *
 * Only the end of the player's whole protocol counts — every retry of every address spent — and
 * only when the fault is the channel's. Never: the account (expired, connection limit), the
 * device (a format it cannot decode), no internet, the free-time meter, or a press-to-play.
 */
const CHANNEL_FAULT_CODES = new Set(["unavailable", "timeout", "manifest", "network"]);

/** Refusals that are about the ACCOUNT, not the channel (native.ts describeFailure). */
const ACCOUNT_STATUSES = new Set([401, 403, 458, 511, 512, 513]);

/** How recently another play must have worked for a failure to say anything about the channel. */
export const PROVIDER_WORKING_MS = 30 * 60 * 1000;

export function isChannelFault(error: PlayerError, online: boolean): boolean {
  if (!online) return false;
  if (!CHANNEL_FAULT_CODES.has(error.code)) return false;
  const status = error.httpStatus ?? Number(/HTTP (\d{3})/.exec(error.message)?.[1] ?? NaN);
  return !ACCOUNT_STATUSES.has(status);
}

interface FailureSink {
  failed: (channelId: string, at: number, status: number | null) => void;
  played: (channelId: string, at: number) => void;
}

/**
 * A failure counts only while the provider is known to be working: some channel played within
 * PROVIDER_WORKING_MS before it, or plays within that long after it (held until then). A provider
 * or network that is down fails every channel, and none of those failures counts.
 */
export function createFailureTracker(sink: FailureSink) {
  let lastPlayedAt = Number.NEGATIVE_INFINITY;
  const held = new Map<string, { at: number; status: number | null }>();
  return {
    played(channelId: string, at: number): void {
      lastPlayedAt = at;
      sink.played(channelId, at);
      for (const [id, failure] of held) {
        if (id !== channelId && at - failure.at <= PROVIDER_WORKING_MS) sink.failed(id, failure.at, failure.status);
      }
      held.clear();
    },
    failed(channelId: string, at: number, status: number | null): void {
      if (at - lastPlayedAt <= PROVIDER_WORKING_MS) sink.failed(channelId, at, status);
      else held.set(channelId, { at, status });
    },
  };
}

/**
 * Watch the player for the rest of the app's life: a live channel that plays, or ends in an error,
 * is reported to the health store. The channel is the one the call site recorded just before
 * playing it (pushRecent), matched by name as previousChannel does.
 */
export function watchChannelFailures(): () => void {
  const health = () => useChannelHealthStore.getState();
  const tracker = createFailureTracker({
    failed: (id, at, status) => health().recordFailedTry(id, { at, status }),
    played: (id) => health().recordPlayed(id),
  });
  let lastStatus = usePlayerStore.getState().status;
  return usePlayerStore.subscribe((state) => {
    const status = state.status;
    if (status === lastStatus) return;
    lastStatus = status;
    const source = state.source;
    if (source?.type !== "live" || (status !== "playing" && status !== "error")) return;
    const channel = state.recentChannels.find((c) => c.name === source.title);
    if (!channel) return;
    if (status === "playing") {
      tracker.played(channel.id, Date.now());
    } else if (state.error && isChannelFault(state.error, typeof navigator === "undefined" || navigator.onLine !== false)) {
      tracker.failed(channel.id, Date.now(), state.error.httpStatus ?? null);
    }
  });
}
